<template>
  <div :class="['chatContainer', { isArchiveView }]">
    <SystemNotification />
    <div
      id="chatContent"
      class="content"
      :class="{
        adjustHeight: (!typer && ['READY', 'CONVERSATION', 'CONVERSATION_SUSPENDED', 'CONVERSATION_NEW'].indexOf(conversationState) > -1),
        fullHeight: $mqAliases.isMobile,
        'archive-view': (isArchiveView && (webView || $mqAliases.isMobile))
        }"
        v-scroller
        :manual-scroll="manualScroll"
      >
      <ArchivedConversationsList v-if="isCustomerFlow && !showAiaiBotDisclaimer" />
      <template
        v-if="conversation && stateIsValid && !isArchiveView && !showAiaiBotDisclaimer">
        <div class="messages-margin" v-if="!showAiaiBotDisclaimer" />
        <Message
          v-for="message in conversationMessages"
          :key="message.msgId"
          :type="getMessageType(message)"
          :message="message"
          :is-incoming="isIncoming(message, user)"
          @scrollToEnd="$emit('scrollToEnd')"
        />
        <div class="messages-margin" v-if="!showAiaiBotDisclaimer"/>
      </template>
      <template v-if="!restoringSession && !isArchiveView">
        <div class="messages-margin" v-if="!showAiaiBotDisclaimer"/>
        <ErrorMessages />
        <div class="messages-margin" v-if="!showAiaiBotDisclaimer"/>
      </template>
      <AiaiBotDisclaimer v-if="showAiaiBotDisclaimer" />
      <Disclaimer
        class="disclaimer"
        :retention="retention"
        v-if="!restoringSession && conversationState === 'READY' && !isCustomerFlow"
        @reset="$emit('reset')"
      />
      <TypingIndicator
        class="typing"
        :show="!isArchiveView && (agentIsTyping || waitingOnAiaiBot)"
        :avatar="aiaiBotIncomingIcon"
        :name="agentScreenName"
        :no-background="waitingOnAiaiBot"
        @scrollToEnd="typingScroll"
      />
      <loading v-if="showLoader" class="loading"/>
    </div>
    <Typer
      v-if="typer"
      emojis
      icon="send"
      @scrollToEnd="$emit('scrollToEnd')"
    />
  </div>
</template>

<script>
import { debounce } from 'lodash'
import vueLinkify from 'vue-linkify'
import { mapState, mapGetters, mapActions } from 'vuex'
import ArchivedConversationsList from '@/components/archivedConversations/ArchivedConversationsList'
import TypingIndicator from '@/components/TypingIndicator'
import AiaiBotDisclaimer from './aiaiBot/AiaiBotDisclaimer'
import Message from './Message'
import Disclaimer from './Disclaimer'
import Typer from './Typer'
import ErrorMessages from './ErrorMessages'
import Loading from './Loading'
import SystemNotification from './SystemNotification'

const validStates = {
  authenticated: [
    'READY',
    'CONVERSATION',
    'CONVERSATION_SUSPENDED',
    'OUTSIDE_SERVICE_HOURS',
    'NO_AGENT_AVAILABLE',
    'CONVERSATION_NEW',
    'STARTING_NEW_CONVERSATION'
  ],
  anonymous: [
    'READY',
    'CONVERSATION',
    'CONVERSATION_SUSPENDED',
    'CONVERSATION_NEW',
    'STARTING_NEW_CONVERSATION'
  ]
}

export default {
  name: 'ChatContainer',
  components: {
    Loading,
    TypingIndicator,
    ErrorMessages,
    Typer,
    Disclaimer,
    AiaiBotDisclaimer,
    Message,
    SystemNotification,
    ArchivedConversationsList
  },
  directives: {
    vueLinkify
  },
  props: {
    retention: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      linkAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    }
  },

  created () {
    setTimeout(() => {
      this.$emit('hideLoader')
      this.loadLastActivityMessage()
    }, 20)
  },

  methods: {
    ...mapActions('conversation', [
      'addMessage',
      'resetUnreadMessages',
      'updateMessagesStatus',
      'setLastCheckActivityMsgStatus',
      'sendMessage'
    ]),
    getMessageType (message) {
      if (message.file) {
        return 'FILE'
      }
      return message.msgType
    },
    isIncoming (message, user) {
      if (message.dummy_user === true) {
        return false
      } else {
        return Number(message.senderId) !== Number(user.userId)
      }
    },
    scrollToEnd () {
      setTimeout(() => {
        this.$emit('scrollToEnd')
      }, 50)
    },
    typingScroll () {
      if (!this.windowScrolledUp) {
        setTimeout(() => {
          this.$emit('scrollToEnd')
        }, 100)
      }
    },
    getLastCheckActivityMsg (messages) {
      if (!messages) return {}
      let checkActivity = {}
      messages.forEach((message, index) => {
        if (message.msgType === 'CHECK_ACTIVITY') {
          checkActivity = {
            message,
            index
          }
        }
      })
      for (let i = checkActivity.index; i < messages.length; i++) {
        if (parseInt(messages[i].senderId) === this.user.userId) {
          return {}
        }
      }
      return checkActivity.message || {}
    },
    loadLastActivityMessage () {
      if (this.conversation && this.conversation.messages) {
        const lastCheckActivityMsg = this.getLastCheckActivityMsg(this.conversation.messages)
        this.setLastCheckActivityMsgStatus(lastCheckActivityMsg)
      }
    },
    initializeAiaiBot () {
      this.sendMessage({
        msgType: 'AIAIBOT_INITIALIZE'
      })
    },
    endConversation () {
      this.$chatApi.endConversation(this.conversation)
    }
  },

  computed: {
    ...mapState('uistate', ['windowScrolledUp', 'conversationState', 'restoringSession', 'agentAvatar', 'agentScreenName', 'isArchiveView', 'loadingLabels', 'outsideServiceHoursDueToHoliday']),
    ...mapState('conversation', ['conversation', 'user', 'typerActive', 'chatbotActive', 'showAiaiBotInput', 'waitingOnAiaiBot']),
    ...mapGetters('uistate', ['agentIsTyping', 'isCustomerFlow', 'isAiaiBotActive', 'webView', 'aiaiBotIncomingIcon']),
    ...mapGetters('conversation', ['isUserAuthenticated', 'startMessages']),
    userMessages () {
      return this.conversation && this.conversation.messages.filter(message => parseInt(message.senderId) !== this.user.userId && parseInt(message.senderId) > 0)
    },
    showLoader () {
      return this.restoringSession ||
        this.loadingLabels ||
        ['INIT', 'STARTING_NEW_CONVERSATION'].includes(this.conversationState)
    },
    typer () {
      if (this.chatbotActive || this.isArchiveView) {
        return false
      }
      if (this.isAiaiBotActive && !this.showAiaiBotInput) {
        return false
      }
      if (this.isCustomerFlow) {
        if ([
          'READY',
          'CONVERSATION',
          'CONVERSATION_SUSPENDED',
          'OUTSIDE_SERVICE_HOURS',
          'NO_AGENT_AVAILABLE',
          'CONVERSATION_NEW'
        ].includes(this.conversationState)) {
          return !(this.conversation && ([
            'AGENT_ENDED',
            'AIAIBOT_ENDED'
          ].includes(this.conversation.status)))
        }
      }
      return this.conversation && this.stateIsValid && this.typerActive
    },
    manualScroll: {
      get () {
        return this.$store.state.uistate.windowScrolledUp
      },
      set: debounce(function (value) {
        this.$store.commit('uistate/SET_WINDOW_SCROLLED_UP', value)
      }, 100)
    },
    showTerms () {
      if (this.isCustomerFlow) return false
      if (this.conversationState === 'READY' && !this.conversation) {
        return true
      }
      if (this.conversation && this.conversationState === 'READY' && !this.typerActive) {
        return true
      }
      return false
    },
    stateIsValid () {
      if (this.isUserAuthenticated) {
        return validStates.authenticated.includes(this.conversationState)
      }

      return validStates.anonymous.includes(this.conversationState)
    },
    showAiaiBotDisclaimer () {
      return this.isAiaiBotActive &&
        !this.restoringSession &&
        validStates.authenticated.includes(this.conversationState) &&
        this.conversation?.convId === -1
    },
    conversationMessages () {
      return [
        ...(this.showAiaiBotDisclaimer ? [] : this.startMessages),
        ...this.conversation.messages || []
      ]
    }
  },
  watch: {
    manualScroll (value) {
      if (!value) {
        this.$emit('showNewMessageAlert')
        this.resetUnreadMessages()
        if (this.conversation) {
          this.updateMessagesStatus()
        }
      }
    },
    conversation (value) {
      if (value) {
        this.loadLastActivityMessage()

        if (value.convId &&
          this.isAiaiBotActive &&
          value.conversationType === 'AIAIBOT' &&
          !value.aiaiBotSessionId
        ) {
          this.initializeAiaiBot()
        }
      }
    }
  }
}
</script>

<style scoped lang="postcss">
.chatContainer {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 1 2 auto;
  position: relative;

  &.isArchiveView {
    padding-bottom: 50px;
  }

  .content {
    flex: auto;
    overflow-y: auto;
    display: block;

    .loading {
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 100%;
      z-index: 1;
    }

    :first-child {
      &.messageContainer {
         margin-top: 7.5px;
      }
    }
  }

  .messages-margin {
    height: 10px;
  }

  .archive-view {
    margin-bottom: 54px;
  }

  .termsContainer {
    background-color: #3c3a38;
    padding: 8px;
    .terms {
      overflow: auto;
      overflow-x: hidden;
      height:140px;
      font-size: 12px;
      color: white;

      h3 {
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: bold;
        color: var(--c-original-green);
        margin-bottom: 8px;
      }

      >>> p {
        font-size: 12px;
        color: white;
        margin-bottom: 8px;
      }

      >>> a {
        font-size: 12px;
        word-break: break-all;
        &:hover {
          border: none;
        }
      }
    }
  }

  .typing {
    margin-bottom: 20px;
  }
}
</style>
